import React, { useState } from "react";
import Calendar from "react-calendar";
import "./CalendarView.css";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";

const CalendarView = ({ onSelectEvent, onMonthChange, events, festivities = [] }) => {
    const { language } = useLanguage();
    const [date, setDate] = useState(new Date());

    const formatLocalDate = (date) => {
        return `${date.getFullYear()}-${
            String(date.getMonth() + 1).padStart(2, '0')}-${
            String(date.getDate()).padStart(2, '0')}`;
    };
    
    const handleDateSelect = (selectedDate) => {
        // Check if there's a festivity on this date
        const formattedDate = formatLocalDate(selectedDate);
        const festivityOnDate = festivities.find(f => 
            formatLocalDate(new Date(f.date)) === formattedDate);
            
        // If there's a festivity, create a virtual event for the festivity
        if (festivityOnDate) {
            const festivityEvent = {
                id: `festivity-${festivityOnDate.id}`,
                title: festivityOnDate.name,
                date: formattedDate,
                time: "00:00",
                event_type: "catholic_festivity",
                description: festivityOnDate.description || "",
                location: "",
                isFestivity: true, // Mark as festivity for special handling
                festivity_data: festivityOnDate // Store full festivity data
            };
            
            // Pass festivity to parent
            onSelectEvent(selectedDate, festivityEvent);
        } else {
            // No festivity, handle normally
            onSelectEvent(selectedDate);
        }
    };
    
    const tileClassName = ({ date: tileDate, view }) => {
        if (view === "month") {
            const formattedTileDate = formatLocalDate(tileDate);
            const classes = [];
            
            // Check for regular events
            if (Array.isArray(events)) {
                const hasEvent = events.some(event => {
                    const eventDate = event.date ? formatLocalDate(new Date(event.date)) : null;
                    return eventDate === formattedTileDate;
                });
                
                if (hasEvent) {
                    classes.push('event-day');
                }
            }
            
            // Check for Catholic festivities
            if (Array.isArray(festivities)) {
                const hasFestivity = festivities.some(festivity => {
                    const festivityDate = festivity.date ? 
                        formatLocalDate(new Date(festivity.date)) : null;
                    return festivityDate === formattedTileDate;
                });
                
                // Get specific festivity to determine its type/importance
                const festivity = festivities.find(f => 
                    formatLocalDate(new Date(f.date)) === formattedTileDate);
                
                if (hasFestivity && festivity) {
                    if (festivity.is_holy_day) {
                        classes.push('holy-day-festivity');
                    } else if (festivity.festivity_type === 'solemnity') {
                        classes.push('solemnity-festivity');
                    } else if (festivity.festivity_type === 'feast') {
                        classes.push('feast-festivity');
                    } else {
                        classes.push('regular-festivity');
                    }
                    
                    // Add liturgical color class if available
                    if (festivity.color) {
                        classes.push(`liturgical-${festivity.color}`);
                    }
                }
            }
            
            return classes.join(' ');
        }
        return "";
    };

    // Modify calendar tile content to add festivity indicators
    const tileContent = ({ date: tileDate, view }) => {
        if (view === "month" && Array.isArray(festivities)) {
            const formattedTileDate = formatLocalDate(tileDate);
            
            // Check for Catholic festivities
            const festivity = festivities.find(f => 
                formatLocalDate(new Date(f.date)) === formattedTileDate);
            
            if (festivity) {
                return (
                    <div className="festivity-indicator" title={festivity.name}>
                        {festivity.is_holy_day && <span className="holy-day-dot">•</span>}
                    </div>
                );
            }
        }
        return null;
    };

    // Determine locale based on language
    const calendarLocale = language === 'sl' ? 'sl-SI' : 'en-US';

    return (
        <div className="w-full mx-auto bg-white rounded-xl overflow-hidden" style={{ boxShadow: 'none' }}>
            <Calendar
                locale={calendarLocale}
                onChange={setDate}
                value={date}
                tileClassName={tileClassName}
                tileContent={tileContent}
                onClickDay={handleDateSelect}
                onActiveStartDateChange={({ activeStartDate }) => onMonthChange(activeStartDate)}
                className="w-full p-1"
                prevLabel={<ChevronLeft size={16} className="text-gray-600" />}
                nextLabel={<ChevronRight size={16} className="text-gray-600" />}
                calendarClassName="w-full border-none"
                showNavigation={true}
                navigationLabel={({ date }) => (
                    <span className="font-medium text-gray-800" style={{ fontSize: '0.9rem' }}>
                        {date.toLocaleDateString(calendarLocale, { month: 'long', year: 'numeric' })}
                    </span>
                )}
            />
        </div>
    );
};

export default CalendarView;